import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';

interface CancelOrderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orderId: string;
  onCancel: (orderId: string, reason: string) => Promise<void>;
}

export function CancelOrderDialog({
  open,
  onOpenChange,
  orderId,
  onCancel,
}: CancelOrderDialogProps) {
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const { t } = useTranslation();

  const handleSubmit = async () => {
    if (!reason.trim()) {
      toast({
        title: t('reasonRequired'),
        description: t('reasonRequiredDescription'),
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      await onCancel(orderId, reason);

      toast({
        title: t('orderCancelled'),
        description: t('orderCancelledDescription'),
      });

      onOpenChange(false);
      setReason('');
    } catch (error) {
      toast({
        title: t('error'),
        description: t('cancelOrderError'),
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('cancelOrder')}</DialogTitle>
          <DialogDescription>{t('cancelOrderConfirmation')}</DialogDescription>
        </DialogHeader>

        <div className='grid gap-4 py-4'>
          <div className='grid gap-2'>
            <Label htmlFor='reason'>{t('reasonForCancellation')}</Label>
            <Textarea
              id='reason'
              placeholder={t('reasonRequiredDescription')}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant='outline'
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}>
            {t('cancel')}
          </Button>
          <Button
            variant='destructive'
            onClick={handleSubmit}
            disabled={isSubmitting || !reason.trim()}>
            {isSubmitting ? t('cancelling') : t('confirmCancellation')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
